import { I18n } from '@/i18n/I18n';
import { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Offcanvas } from 'react-bootstrap';
import { ChevronDown, Plus, Trash2, X } from 'lucide-react';
import { VerificationCriteria } from '@/backend/models/verificationCriteria';
import {
  VerificationCriteriaTaskActionStatus,
  VerificationCriteriaTaskCreateInput,
  VerificationCriteriaTaskUpdateInput,
} from '@/backend/access/verificationCriteriaTask/types';
import { VerificationCriteriaStatus } from '@/backend/access/verificationCriteria/types';
import { useRequirementsStore } from '@/frontend/store/RequirementsStore';
import { useShallow } from 'zustand/react/shallow';

interface DraftTask {
  id: string;
  description: string;
  actionStatus: VerificationCriteriaTaskActionStatus;
  isNew: boolean;
  isUpdated: boolean;
}

interface VerificationCriteriaPanelProps {
  onHide: () => void;
  requirementId: string;
  verificationCriteria?: VerificationCriteria;
}

export function VerificationCriteriaPanel({
  onHide,
  requirementId,
  verificationCriteria,
}: VerificationCriteriaPanelProps) {
  const isUpdate = !!verificationCriteria;
  const titleAndDescriptionLocked = isUpdate && verificationCriteria!.status !== VerificationCriteriaStatus.PENDING;
  const existingTasks = useRequirementsStore(useShallow(state => state.verificationCriteriaTasks.filter(t => t.verificationCriteriaId === verificationCriteria?.id)));
  const initialDrafts = useMemo(() => existingTasks.map(task => ({
    ...task, isNew: false, isUpdated: false
  })), [existingTasks]);

  const createVerificationCriteria = useRequirementsStore(s => s.createVerificationCriteria);
  const updateVerificationCriteria = useRequirementsStore(s => s.updateVerificationCriteria);
  const createVerificationCriteriaTasks = useRequirementsStore(s => s.createVerificationCriteriaTasks);
  const updateVerificationCriteriaTasks = useRequirementsStore(s => s.updateVerificationCriteriaTasks);
  const deleteVerificationCriteriaTasks = useRequirementsStore(s => s.deleteVerificationCriteriaTasks);

  const [title, setTitle] = useState(verificationCriteria?.title ?? '');
  const [description, setDescription] = useState(verificationCriteria?.description ?? '');
  const [titleError, setTitleError] = useState('');
  const [drafts, setDrafts] = useState<DraftTask[]>(initialDrafts);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setTitle(verificationCriteria?.title ?? '');
    setDescription(verificationCriteria?.description ?? '');
    setTitleError('');
    setDrafts(initialDrafts);
  }, [verificationCriteria, existingTasks]);

  const handleAddTask = () => {
    setDrafts(prev => [
      ...prev,
      {
        id: `draft-${Math.random().toString(36).slice(2)}-${Date.now()}`,
        description: '',
        actionStatus: VerificationCriteriaTaskActionStatus.ACCEPTED,
        isNew: true,
        isUpdated: false,
      },
    ]);
  };

  const handleRemoveTask = (id: string) => {
    setDrafts(prev => prev.filter(t => t.id !== id));
  };

  const handleUpdateTask = (id: string, patch: Partial<DraftTask>) => {
    setDrafts(prev => prev.map(t => (t.id === id ? { ...t, ...patch, isUpdated: true } : t)));
  };

  const computeTaskChanges = () => {
    const newTasks: VerificationCriteriaTaskCreateInput[] = [];
    const updatedTasks: VerificationCriteriaTaskUpdateInput[] = [];

    drafts.forEach(draft => {
      if (draft.isNew) {
        newTasks.push(draft);
        return;
      }

      if(draft.isUpdated) {
        updatedTasks.push(draft);
        return;
      }
    });

    const deletedIds: string[] = existingTasks
      .filter(task => !drafts.some(draft => draft.id === task.id))
      .map(task => task.id);

    return { newTasks, updatedTasks, deletedIds };
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setTitleError(I18n.get('requiredFieldError', { field: I18n.get('title') }));
      return;
    }

    setIsSubmitting(true);
    try {
      if (verificationCriteria) {
        const titleChanged = verificationCriteria.title !== title.trim();
        const descriptionChanged = verificationCriteria.description !== description.trim();
        if (titleChanged || descriptionChanged) {
          await updateVerificationCriteria({
            ...verificationCriteria,
            title: title.trim(),
            description: description.trim(),
          });
        }

        const { newTasks, updatedTasks, deletedIds } = computeTaskChanges();
        await Promise.all([
          createVerificationCriteriaTasks(verificationCriteria.id, newTasks),
          updateVerificationCriteriaTasks(updatedTasks),
          deleteVerificationCriteriaTasks(deletedIds),
        ]);
      } else {
        const created = await createVerificationCriteria({
          title: title.trim(),
          description: description.trim(),
          requirementId,
        });
        if (created) {
          await createVerificationCriteriaTasks(created.id, drafts);
        }
      }
      onHide();
    } finally {
      setIsSubmitting(false);
    }
  };

  const heading = isUpdate ? I18n.get('editVerificationCriteria') : I18n.get('addVerificationCriteria');
  const subtitle = isUpdate
    ? I18n.get('editVerificationCriteriaSubtitle')
    : I18n.get('addVerificationCriteriaSubtitle');
  const submitLabel = isUpdate ? I18n.get('saveCriteria') : I18n.get('addCriteria');

  return (
    <Offcanvas
      show
      onHide={onHide}
      placement="end"
      backdrop={false}
      scroll
      className="side-panel"
      aria-labelledby="verificationCriteriaPanelLabel"
    >
      <Offcanvas.Body className="side-panel-container d-flex flex-column">
        <div>
          <button
            className="btn-blue-light fusa-btn-medium-icon"
            onClick={onHide}
            aria-label={I18n.get('close')}
            disabled={isSubmitting}
          >
            <X />
          </button>
        </div>
        <h4 className="panel-heading mt-4">
          {heading}
        </h4>
        <p className="verification-criteria-panel-subtitle">{subtitle}</p>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="form-label-neutral">{I18n.get('title')}</Form.Label>
            <Form.Control
              className="form-control-neutral"
              type="text"
              placeholder={I18n.get('verificationCriteriaTitlePlaceholder')}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleError('');
              }}
              isInvalid={!!titleError}
              disabled={titleAndDescriptionLocked}
              aria-label={I18n.get('title')}
            />
            <Form.Control.Feedback type="invalid">{titleError}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="form-label-neutral">
              {I18n.get('description')} ({I18n.get('optional')})
            </Form.Label>
            <Form.Control
              className="form-textarea-neutral"
              as="textarea"
              rows={3}
              placeholder={I18n.get('verificationCriteriaDescriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={titleAndDescriptionLocked}
              aria-label={I18n.get('description')}
            />
          </Form.Group>
        </Form>

        <TasksSection
          drafts={drafts}
          onAddTask={handleAddTask}
          onRemoveTask={handleRemoveTask}
          onUpdateTask={handleUpdateTask}
          disabled={isSubmitting} 
        />
        <div className="mt-4">
          <Button
            className="fusa-btn-medium fusa-btn-primary"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting || !title.trim()}
          >
            {submitLabel}
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

interface TasksSectionProps {
  drafts: DraftTask[];
  onAddTask: () => void;
  onRemoveTask: (id: string) => void;
  onUpdateTask: (id: string, patch: Partial<DraftTask>) => void;
  disabled?: boolean;
}

function TasksSection({ drafts, onAddTask, onRemoveTask, onUpdateTask, disabled }: TasksSectionProps) {
  return (
    <div className="list-card my-3" style={{ width: '600px' }} aria-label={I18n.get('tasks')}>
      <div className="list-card-title">{I18n.get('tasks')}</div>
      {drafts.length === 0 ? (
        <div className="empty-list-card-content">
          <Button
            className="fusa-btn-medium-with-icon fusa-btn-primary"
            onClick={onAddTask}
            disabled={disabled}
            aria-label={I18n.get('addTask')}
          >
            <Plus size={20} />
            {I18n.get('addTask')}
          </Button>
          <p className="list-card-help-text">{I18n.get('noTasksHelper')}</p>
        </div>
      ) : (
        <>
          <div className="d-flex flex-column gap-1">
            {drafts.map(draft => (
              <TaskRow
                key={draft.id}
                draft={draft}
                onRemove={() => onRemoveTask(draft.id)}
                onUpdate={(patch) => onUpdateTask(draft.id, patch)}
                disabled={disabled}
              />
            ))}
          </div>
          <Button
            className="fusa-btn-small-with-icon fusa-btn-primary mt-2"
            onClick={onAddTask}
            disabled={disabled}
            aria-label={I18n.get('addTask')}
          >
            <Plus size={16} />
            {I18n.get('addTask')}
          </Button>
        </>
      )}
    </div>
  );
}

interface TaskRowProps {
  draft: DraftTask;
  onRemove: () => void;
  onUpdate: (patch: Partial<DraftTask>) => void;
  disabled?: boolean;
}

function TaskRow({ draft, onRemove, onUpdate, disabled }: TaskRowProps) {
  const statusClass =
    draft.actionStatus === VerificationCriteriaTaskActionStatus.ACCEPTED
      ? 'verification-criteria-task-status-accepted'
      : 'verification-criteria-task-status-rejected';

  return (
    <div className="list-card-item verification-criteria-task-row">
      <Form.Control
        as="textarea"
        rows={1}
        className="verification-criteria-task-description"
        placeholder={I18n.get('taskDescriptionPlaceholder')}
        value={draft.description}
        onChange={(e) => onUpdate({ description: e.target.value })}
        aria-label={I18n.get('taskDescriptionPlaceholder')}
        disabled={disabled}
      />
      <Dropdown>
        <Dropdown.Toggle
          className={`fusa-menu-btn ${statusClass}`}
          aria-label={I18n.get('actionStatus')}
          disabled={disabled}
        >
          {I18n.get(draft.actionStatus)}
          <ChevronDown size={16} />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item
            onClick={() => onUpdate({ actionStatus: VerificationCriteriaTaskActionStatus.ACCEPTED })}
          >
            {I18n.get('accepted')}
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => onUpdate({ actionStatus: VerificationCriteriaTaskActionStatus.REJECTED })}
          >
            {I18n.get('rejected')}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <Button
        className="fusa-btn-small-with-icon btn-neutral-light"
        onClick={onRemove}
        aria-label={I18n.get('deleteTask')}
        disabled={disabled}
      >
        <Trash2 size={16} />
      </Button>
    </div>
  );
}
